class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);


class CartItems extends HTMLElement {
  constructor() {
    super();
    this.outOfStockToken = window.outOfStockToken;
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      if (!event.target.classList.contains('noBubbling')) {
        this.onChange(event);
      }
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // Prevent default checkout behavior globally
    document.addEventListener('click', (event) => {
      const isCheckoutLink = event.target.matches('a[href="/checkout"]') || 
                            event.target.matches('#CartDrawer-Checkout');
      if (isCheckoutLink) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleCheckout();
      }
    }, true);
  }

  async handleCheckout() {
    try {
      CartItems.markCheckoutNavigation();

      // Disable all checkout buttons
      const checkoutBtns = document.querySelectorAll('.cart__ctas #CartDrawer-Checkout, a[href="/checkout"]');
      checkoutBtns.forEach(btn => {
        btn.setAttribute('disabled', 'disabled');
        btn.style.opacity = '0.5';
      });

      // Show loading state
      this.enableLoading();

      // Run validation and wait for it to complete
      await this.validateOOS();

      // Re-enable buttons
      checkoutBtns.forEach(btn => {
        btn.removeAttribute('disabled');
        btn.style.opacity = '1';
      });

    } catch (error) {
      console.error('Checkout error:', error);
      this.disableLoading();
      
      // Re-enable buttons on error
      checkoutBtns.forEach(btn => {
        btn.removeAttribute('disabled');
        btn.style.opacity = '1';
      });
    }
  }

  async validateOOS() {
    try {
      const cart = await AjaxCart.getCart();
      if (!cart.items.length) {
        this.disableLoading();
        return;
      }

      const variantIds = cart.items.map(item => item.variant_id);
      const query = `
        {
          nodes(ids: [${variantIds.map(id => `"gid://shopify/ProductVariant/${id}"`).join(',')}]) {
            ... on ProductVariant {
              id
              title
              product {
                tags
              }
              metafield(namespace: "custom", key: "out_of_stock") {
                value
              }
            }
          }
        }
      `;

      const response = await fetch('/api/2023-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.outOfStockToken
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();
      const outOfStockItems = [];
      const outOfStockParentUIDs = new Set();
      let pass = false;

      data.data.nodes.forEach((node, i) => {
        if (!pass) {
          pass = node?.product?.tags?.includes('OutOfStockPass');
        }
      });

      // Process OOS items
      data.data.nodes.forEach((node, i) => {
        // Check both metafield and OutOfStock tag
        const hasOOSMetafield = node?.metafield?.value === '1';
        const hasOOSTag = node?.product?.tags?.includes('OutOfStock');

        if (!pass) {
          if (hasOOSMetafield || hasOOSTag) {
            const cartItem = cart.items[i];
            const parentUid = cartItem.properties?._parent_uid ? String(cartItem.properties._parent_uid) : null;

            outOfStockItems.push({
              title: node.title,
              key: cartItem.key
            });

            if (parentUid) {
              outOfStockParentUIDs.add(parentUid);
            }
          }
        }
      });

      // Find related gifts
      if (!pass) {
        cart.items.forEach(cartItem => {
          const uid = cartItem.properties?._uid ? String(cartItem.properties._uid) : null;
          if (uid && outOfStockParentUIDs.has(uid)) {
            outOfStockItems.push({
              title: cartItem.title,
              key: cartItem.key
            });
          }
        });
      }

      console.log('outOfStockItems', outOfStockItems);

      // Remove items if needed
      if (outOfStockItems.length > 0) {
        const updates = {};
        outOfStockItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Update cart and wait for completion
        await this.updateItems(updates);
        
        // Ensure changes are processed
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Run additional cart logic
        await CartProgress.checkCartProgress();
        
        // Get final cart state
        const finalCart = await AjaxCart.getCart();
        
        // Only proceed if items remain
        if (finalCart.items.length > 0) {
          this.disableLoading();
          window.location.href = '/checkout';
        } else {
          this.disableLoading();
          window.location.reload();
        }
      } else {
        // No OOS items, proceed to checkout
        this.disableLoading();
        window.location.href = '/checkout';
      }

    } catch (error) {
      console.error('Error in validateOOS:', error);
      this.disableLoading();
      throw error;
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.ctas = document.querySelectorAll('#CartDrawer-Checkout, drawer__totals-checkout a');
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      // Birthday apply refreshes the drawer once after the code sticks — skip the
      // intermediate redraw that flashes the discount chip and jumps scroll.
      if (event.source === 'birthday-sale-discount') {
        return;
      }
      if (
        document.documentElement.hasAttribute('data-birthday-applying-code') &&
        (event.source === 'discount-code-applied' || event.source === 'product-form')
      ) {
        return;
      }
      if (
        event.source === 'product-form' &&
        window.BirthdaySalePDP &&
        typeof window.BirthdaySalePDP.isActive === 'function' &&
        window.BirthdaySalePDP.isActive()
      ) {
        // ATC already calls cart.renderContents; birthday finish refreshes once with code.
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);

    if (input) {
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    } else {
      this.onCartUpdate();
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  static cartDrawerRenderContent() {
    if (CartItems._drawerRenderInFlight) {
      CartItems._drawerRenderQueued = true;
      return CartItems._drawerRenderInFlight;
    }

    CartItems._drawerRenderInFlight = CartItems.refreshCartDrawerSections({ openDrawer: true }).finally(() => {
      CartItems._drawerRenderInFlight = null;
      if (CartItems._drawerRenderQueued) {
        CartItems._drawerRenderQueued = false;
        CartItems.cartDrawerRenderContent();
      }
    });

    return CartItems._drawerRenderInFlight;
  }

  static markCheckoutNavigation() {
    try {
      sessionStorage.setItem('cart-checkout-navigation', '1');
      sessionStorage.removeItem('cart-drawer-refreshed-after-checkout');
      const codes = Array.from(document.querySelectorAll('.js-remove-discount-button'))
        .map((el) => el.dataset.discountCode)
        .filter(Boolean);
      if (codes.length) {
        sessionStorage.setItem('cart-checkout-discount-codes', JSON.stringify(codes));
      }
    } catch (error) {
      // Ignore storage errors (e.g. private browsing restrictions).
    }
  }

  static getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  static collectDiscountCodes() {
    const codes = new Set();
    const birthdayDismissed =
      typeof window.BirthdaySalePDP?.isCodeDismissed === 'function' &&
      window.BirthdaySalePDP.isCodeDismissed();
    const isBirthdayCode = (code) =>
      typeof window.BirthdaySalePDP?.isSegmentDiscountCode === 'function' &&
      window.BirthdaySalePDP.isSegmentDiscountCode(code);

    const cookieCode = CartItems.getCookie('discount_code');
    if (cookieCode && !(birthdayDismissed && isBirthdayCode(cookieCode))) {
      codes.add(cookieCode);
    }

    try {
      const saved = sessionStorage.getItem('cart-checkout-discount-codes');
      if (saved) {
        JSON.parse(saved).forEach((code) => {
          if (birthdayDismissed && isBirthdayCode(code)) return;
          codes.add(code);
        });
      }
    } catch (error) {
      // Ignore storage errors.
    }

    document.querySelectorAll('.js-remove-discount-button').forEach((el) => {
      if (!el.dataset.discountCode) return;
      if (birthdayDismissed && isBirthdayCode(el.dataset.discountCode)) return;
      codes.add(el.dataset.discountCode);
    });

    return [...codes];
  }

  static async fetchStorefrontDiscountCodes() {
    const cartToken = CartItems.getCookie('cart');
    if (!cartToken || !window.storefrontApiToken || !window.shopUrl) return [];

    const query = `{
      cart(id: "gid://shopify/Cart/${cartToken}") {
        discountCodes { code applicable }
      }
    }`;

    try {
      const response = await fetch(`${window.shopUrl}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/graphql',
          'X-Shopify-Storefront-Access-Token': window.storefrontApiToken,
        },
        body: query,
      });
      const data = await response.json();
      return (data?.data?.cart?.discountCodes || [])
        .filter((entry) => entry.applicable && entry.code)
        .map((entry) => entry.code);
    } catch (error) {
      console.error('fetchStorefrontDiscountCodes:', error);
      return [];
    }
  }

  static async applyDiscountCodesToCart(codes) {
    if (!codes.length) return;

    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      const cart = await response.json();
      if (cart?.total_discount > 0) return;
    } catch (error) {
      console.error('applyDiscountCodesToCart:', error);
    }

    await CartItems.applyDiscountCodesViaStorefront(codes);

    // Re-fetch and refresh drawer when discount was applied via Storefront API
    // (Ajax /cart/update.js can miss AIOD tiered re-evaluation on first line item).
    try {
      const cart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((r) => r.json());
      if (cart?.total_discount > 0 && typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'discount-code-applied' });
      }
    } catch (e) {}
  }

  static async applyDiscountCodesViaStorefront(codes) {
    const cartToken = CartItems.getCookie('cart');
    if (!cartToken || !window.storefrontApiToken || !window.shopUrl) return;

    const query = `
      mutation {
        cartDiscountCodesUpdate(
          cartId: "gid://shopify/Cart/${cartToken}",
          discountCodes: ${JSON.stringify(codes)}
        ) {
          cart { id }
          userErrors { message }
        }
      }
    `;

    try {
      await fetch(`${window.shopUrl}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/graphql',
          'X-Shopify-Storefront-Access-Token': window.storefrontApiToken,
        },
        body: query,
      });
    } catch (error) {
      console.error('applyDiscountCodesViaStorefront:', error);
    }
  }

  static async syncCheckoutDiscounts() {
    try {
      const cart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((response) => response.json());
      if (!cart?.item_count) return;

      if (cart.total_discount > 0) return;

      let codes = CartItems.collectDiscountCodes();
      if (!codes.length) {
        codes = await CartItems.fetchStorefrontDiscountCodes();
      }
      if (!codes.length) return;

      await CartItems.applyDiscountCodesToCart(codes);

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'checkout-discount-sync' });
      }
    } catch (error) {
      console.error('syncCheckoutDiscounts:', error);
    }
  }

  static shouldRefreshCartDrawerOnOpen() {
    try {
      if (sessionStorage.getItem('cart-drawer-refreshed-after-checkout') === '1') return false;
      if (sessionStorage.getItem('cart-needs-drawer-refresh') === '1') return true;
    } catch (error) {
      // Ignore storage errors.
    }
    return /checkout/i.test(document.referrer || '');
  }

  static clearCheckoutReturnFlags() {
    try {
      sessionStorage.removeItem('cart-needs-drawer-refresh');
      sessionStorage.removeItem('cart-checkout-navigation');
      sessionStorage.removeItem('cart-checkout-discount-codes');
      sessionStorage.setItem('cart-drawer-refreshed-after-checkout', '1');
    } catch (error) {
      // Ignore storage errors.
    }
  }

  static getHeaderSectionId() {
    const sectionHeader = document.querySelector('.section-header');
    return sectionHeader?.id?.replace?.('shopify-section-', '') || '';
  }

  static applyCartIconBubbleFromHtml(sectionHtml) {
    if (!sectionHtml) return false;

    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceElement = html.querySelector('#cart-icon-bubble');
    if (!sourceElement) return false;

    let targetElement = document.querySelector('#cart-icon-bubble');

    if (targetElement) {
      targetElement.innerHTML = sourceElement.innerHTML;
      targetElement.className = sourceElement.className;
      if (sourceElement.getAttribute('href')) {
        targetElement.setAttribute('href', sourceElement.getAttribute('href'));
      }
    } else {
      const headerIcons = document.querySelector('.header__icons');
      if (!headerIcons) return false;
      headerIcons.appendChild(sourceElement);
      targetElement = sourceElement;
    }

    document.querySelector('cart-drawer')?.setHeaderCartIconAccessibility();
    return true;
  }

  static refreshCartIconBubble({ debounce = true } = {}) {
    if (debounce) {
      clearTimeout(CartItems._refreshCartIconTimer);
      return new Promise((resolve) => {
        CartItems._refreshCartIconTimer = setTimeout(() => {
          CartItems._fetchAndRefreshCartIconBubble().then(resolve);
        }, 50);
      });
    }

    return CartItems._fetchAndRefreshCartIconBubble();
  }

  static _fetchAndRefreshCartIconBubble() {
    const headerSectionId = CartItems.getHeaderSectionId();
    if (!headerSectionId) return Promise.resolve();

    return fetch(`${routes.cart_url}?sections=${headerSectionId}&_=${Date.now()}`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((responseJson) => {
        CartItems.applyCartIconBubbleFromHtml(responseJson[headerSectionId]);
      })
      .catch((error) => {
        console.error('refreshCartIconBubble:', error);
      });
  }

  static getCartDrawerScrollTop() {
    const scrollable = document.querySelector('cart-drawer-items');
    return scrollable ? scrollable.scrollTop : 0;
  }

  static restoreCartDrawerScrollTop(scrollTop) {
    if (scrollTop == null) return;
    const restore = () => {
      const scrollable = document.querySelector('cart-drawer-items');
      if (scrollable) scrollable.scrollTop = scrollTop;
    };
    restore();
    requestAnimationFrame(restore);
    setTimeout(restore, 50);
  }

  static refreshCartDrawerSections({ openDrawer = false } = {}) {
    CartItems.refreshCartDrawerSectionsLatestOpenDrawer = openDrawer;

    if (!CartItems.refreshCartDrawerSectionsPending) {
      CartItems.refreshCartDrawerSectionsPending = new Promise((resolve) => {
        CartItems.refreshCartDrawerSectionsResolve = resolve;
      });
    }

    clearTimeout(CartItems.refreshCartDrawerSectionsTimer);
    CartItems.refreshCartDrawerSectionsTimer = setTimeout(() => {
      const finalOpenDrawer = CartItems.refreshCartDrawerSectionsLatestOpenDrawer;
      const resolve = CartItems.refreshCartDrawerSectionsResolve;
      CartItems.refreshCartDrawerSectionsPending = null;
      CartItems.refreshCartDrawerSectionsResolve = null;
      resolve(CartItems.performRefreshCartDrawerSections({ openDrawer: finalOpenDrawer }));
    }, 200);

    return CartItems.refreshCartDrawerSectionsPending;
  }

  static performRefreshCartDrawerSections({ openDrawer = false } = {}) {
    const cartHeaderItems = document.querySelector('cart-drawer-items');
    const drawerSectionId = cartHeaderItems?.dataset?.id;
    const headerSectionId = CartItems.getHeaderSectionId();
    const sectionsParam = [drawerSectionId, headerSectionId].filter(Boolean).join(',');
    const savedScrollTop = CartItems.getCartDrawerScrollTop();

    if (!sectionsParam) {
      console.error('refreshCartDrawerSections: missing section ids');
      return Promise.resolve();
    }

    return fetch(`${routes.cart_url}?sections=${sectionsParam}&_=${Date.now()}`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((responseJson) => {
        const cartDrawer = document.querySelector('cart-drawer');

        if (drawerSectionId && responseJson[drawerSectionId]) {
          const html = new DOMParser().parseFromString(responseJson[drawerSectionId], 'text/html');
          const sourceElement = html.querySelector('.drawer__inner');
          const targetElement = cartDrawer?.querySelector('.drawer__inner');
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        if (headerSectionId && responseJson[headerSectionId]) {
          CartItems.applyCartIconBubbleFromHtml(responseJson[headerSectionId]);
        }

        if (!cartDrawer) return;

        if (cartDrawer.querySelector('.drawer__inner-empty')) {
          cartDrawer.classList.add('is-empty');
        } else {
          cartDrawer.classList.remove('is-empty');
        }

        // Avoid re-running open() animation when the drawer is already open —
        // that resets scroll and feels like a jump.
        if (openDrawer) {
          if (!cartDrawer.classList.contains('active')) {
            cartDrawer.open();
          }
        } else if (cartDrawer.classList.contains('active')) {
          cartDrawer.close();
        }

        CartItems.restoreCartDrawerScrollTop(savedScrollTop);
        window.drawerAnimatedBar?.();
        window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || cartDrawer);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static async handlePageRestore(event) {
    CartItems.resetLoadingState();

    let wasCheckoutNavigation = false;
    try {
      wasCheckoutNavigation = sessionStorage.getItem('cart-checkout-navigation') === '1';
    } catch (error) {
      // Ignore storage errors.
    }

    const referrerIsCheckout = /checkout/i.test(document.referrer || '');
    const shouldRefresh = event.persisted || wasCheckoutNavigation || referrerIsCheckout;
    if (!shouldRefresh) return;

    try {
      sessionStorage.setItem('cart-needs-drawer-refresh', '1');
    } catch (error) {
      // Ignore storage errors.
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const drawerWasActive = cartDrawer?.classList.contains('active');
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackForward = navEntry?.type === 'back_forward';
    const openDrawer =
      drawerWasActive ||
      referrerIsCheckout ||
      (wasCheckoutNavigation && !isBackForward);

    await CartItems.syncCheckoutDiscounts();
    await CartItems.refreshCartDrawerSections({ openDrawer });
    CartItems.clearCheckoutReturnFlags();
  }

  onCartUpdate() {
    if (this.ctas.length > 0) this.ctas.forEach(btn => btn.setAttribute('disabled', 'disabled'));

    if (this.tagName === 'CART-DRAWER-ITEMS') {
      const savedScrollTop = CartItems.getCartDrawerScrollTop();
      fetch(`${routes.cart_url}?section_id=${this.dataset.id}`)
        .then((response) => response.text())
        .then(async (responseText) => {
          const cart = await AjaxCart.getCart();
          let selectors = ['cart-drawer-items', '.drawer__footer.drawer__wrapper'];

          if (cart?.item_count === 0) {
            selectors = ['.drawer__inner'];
          }

          const html = new DOMParser().parseFromString(responseText, 'text/html');

          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.classList.toggle('is-empty', cart?.item_count === 0);
          }

          CartItems.restoreCartDrawerScrollTop(savedScrollTop);
          window.drawerAnimatedBar();
          window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || document);
          CartItems.refreshCartIconBubble({ debounce: false });
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const headerSectionId = CartItems.getHeaderSectionId();

    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: headerSectionId || 'cart-icon-bubble',
        useCartIconBubble: true,
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    if (this.ctas.length > 0) this.ctas.forEach(btn => btn.setAttribute('disabled', 'disabled'));

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors, true);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections?.[section.section];
          if (!sectionHtml) return;

          if (section.useCartIconBubble) {
            CartItems.applyCartIconBubbleFromHtml(sectionHtml);
            return;
          }

          const root = document.getElementById(section.id);
          if (!root) return;

          const elementToReplace = section.selector
            ? root.querySelector(section.selector) || root
            : root;
          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        });
        window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || document);
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message, false);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        await this.updateFreeItems();

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        if (this.ctas.length > 0) this.ctas.forEach(btn => btn.removeAttribute('disabled'));
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          CartItems.refreshCartIconBubble({ debounce: false });
        }
      });
  }

  async updateFreeItems() {
    if (this.ctas.length > 0) this.ctas.forEach(btn => btn.setAttribute('disabled', 'disabled'));

    const cart = await AjaxCart.getCart();
    const parentUIDs = new Set();
    let cart_items = cart.items;
    let updatedItems  = {};
    let parentUIDArray = [];

    let flag = false;
    let separateProductsBundles = [
      "nutribacs-bundle-mother-child-nutrients-set",
      "nutribacs-bundle-gut-nutrients-duo",
      "nutribacs-bundle-family-nutrients-set",
      "bundle-collagen-gut",
      "collagen-skin-duo",
      "collagen-metabolism-duo"
    ];
  
    cart_items.forEach(item => {
      if (item.properties && item.properties._parent_uid) {
        parentUIDs.add(item.properties._parent_uid);
        parentUIDArray.push({
          handle: item.handle,
          parent_uid: item.properties._parent_uid,
          qty: item.quantity
        });
      }
    });

    cart.items.forEach(item => {      
      if (item.properties && item.properties._uid && !parentUIDs.has(item.properties._uid)) {
        if (separateProductsBundles.includes(item.properties._bought_with) && item.handle == 'welcome-jar') {
          if (!flag) {
            flag = true;
            updatedItems[item.key] = 0;
          }
        } else {
          updatedItems[item.key] = 0;
        }
      }
    });

    for (let parentObject of parentUIDArray) {
      const result = await fetch(`${window.shopUrl}/products/${parentObject.handle}.json`).then(res => res.json());

      let counter = 0;
      let hasBundleTag = false;

      if (result?.product?.tags?.length > 0 && result.product.tags.includes('bundle_2_jars')) {
        hasBundleTag = true;

        cart.items.forEach(cartItem => {
          if (parentObject.parent_uid == cartItem.properties?._uid) {
            counter++;
          }
        });
      }

      cart.items.forEach(cartItem => {
        if (parentObject.parent_uid == cartItem.properties?._uid) {
          updatedItems[cartItem.key] = counter === 1 && hasBundleTag ? parentObject.qty * 2 : parentObject.qty
        }
      });
    }

    if (Object.keys(updatedItems).length > 0) {
      await this.updateItems(updatedItems)
    } else {
      this.disableLoading();
      if (this.ctas.length > 0) this.ctas.forEach(btn => btn.removeAttribute('disabled'));
      document.body.dispatchEvent(new CustomEvent('cart-update'));
    }
  }

  updateItems = async (updates) => {
    let body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    return await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      ...{ body }
    })
      .then(response => {
        return response.json();
      })
      .then(cart => {
        if (!cart.sections) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionHtml = cart.sections[section.section];
          if (!sectionHtml) return;

          if (section.useCartIconBubble) {
            CartItems.applyCartIconBubbleFromHtml(sectionHtml);
            return;
          }

          const root = document.getElementById(section.id);
          if (!root) return;

          const elementToReplace = section.selector
            ? root.querySelector(section.selector) || root
            : root;
          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        });
        window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || document);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        this.disableLoading();
        if (this.ctas.length > 0) this.ctas.forEach(btn => btn.removeAttribute('disabled'));
        document.body.dispatchEvent(new CustomEvent('cart-update'));
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          CartItems.refreshCartIconBubble({ debounce: false });
        }
      });
  }

  updateLiveRegions(line, message, isError) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
    const validationModal = this.querySelector('validation-modal');
    if (validationModal && lineItemError && isError) validationModal.openModal();

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (html == null || html === '') return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  static resetLoadingState() {
    document.getElementById('main-cart-items')?.classList.remove('cart__items--disabled');
    document.getElementById('CartDrawer-CartItems')?.classList.remove('cart__items--disabled');

    document.querySelectorAll('.cart-item .loading__spinner, cart-drawer .loading__spinner').forEach((overlay) => {
      overlay.classList.add('hidden');
    });

    document.querySelectorAll('.cart__ctas #CartDrawer-Checkout, a[href="/checkout"]').forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.style.opacity = '1';
    });
  }
}

customElements.define('cart-items', CartItems);
window.CartItems = CartItems;

if (!window.__cartLoadingStateListenersBound) {
  window.__cartLoadingStateListenersBound = true;

  // Clear loading/disabled state before the page is cached (e.g. navigating to checkout).
  window.addEventListener('pagehide', () => CartItems.resetLoadingState());

  // Refresh stale cart UI after returning from checkout (bfcache or store navigation).
  window.addEventListener('pageshow', (event) => {
    CartItems.handlePageRestore(event);
  });

  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    if (document.querySelector('cart-drawer')) {
      CartItems.refreshCartIconBubble();
    }
  });
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class QuickAddDropdown extends HTMLElement {

  constructor() {
    super();

    this.toggle = null;
    this.inputs = null;
    this.dropdownMenu = null;
    this.submitBtn = null;
  }

  connectedCallback() {
    this.toggle = this.querySelector('.dropdown-toggle');
    this.inputs = this.querySelectorAll('input[type="radio"]');
    this.dropdownMenu = this.querySelector('.dropdown-menu');
    this.submitBtn = this.querySelector('button[type="submit"]');

    this.inputs?.forEach(input =>  input.addEventListener('change', e => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }));

    this.toggle?.addEventListener('click', () => {
      this.classList.toggle('active');
    });

    this.submitBtn?.addEventListener('click', e => this.updateVariants(e));
    window.addEventListener('click', e => {
      if (!e.target.closest('quick-add-dropdown')) {
        this.classList.remove('active');
      }
    });
  }

  async updateVariants(e) {
    e.preventDefault();

    this.submitBtn.setAttribute('disabled', 'disabled');
    this.submitBtn?.querySelector('.loading__spinner')?.classList.remove('hidden');

    const currentCartItem = await AjaxCart.getCartItem(this.dataset.cartItemKey);
    const properties = currentCartItem?.properties || null;
    
    await AjaxCart.changeCart(JSON.stringify({
      id: this.dataset.cartItemKey,
      quantity: 0
    }));

    await AjaxCart.addToCart(JSON.stringify({
      id: this.querySelector('input[type="radio"]:checked').value,
      quantity: 1,
      selling_plan: this.querySelector('input[type="radio"]:checked').dataset.sellingPlanId,
      properties: properties ? properties : null
    }));

    // const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items')
    // if (cartItems) {
    //   cartItems.onCartUpdate();
    // }
  }

}
customElements.define('quick-add-dropdown', QuickAddDropdown);
